import { falAngleRight } from 'shared/@generated/icons/falAngleRight';
import { Props } from 'shared/@types';
import { Text } from 'shared/components/text';
import { View } from 'react-native';
import { ReactNode } from 'react';
import { Link } from 'shared/components/link';
import { Icon } from 'shared/components/icon';
import { clsx } from 'clsx';

const H1 = ({
  title,
  subtitle,
  linkTitle,
  linkHref,
  linkAsHref,
  className,
  right,
  extraLarge = false,
  hero = false,
  marginTop = false,
  marginBottom = true,
  center = false,
  ariaLevel = 1,
  light = false,
}: Props<{
  title: ReactNode | string;
  subtitle?: ReactNode | string;
  linkTitle?: string;
  linkHref?: string;
  linkAsHref?: string;
  right?: ReactNode;
  extraLarge?: boolean;
  hero?: boolean;
  marginTop?: boolean;
  marginBottom?: boolean;
  center?: boolean;
  ariaLevel?: number;
  light?: boolean;
  hideTitleWhenNativeHeader?: boolean;
}>) => {
  return (
    <View className={`${marginTop ? 'mt-9 md:mt-12' : ''} ${marginBottom ? 'mb-3 md:mb-6' : ''}`}>
      <View className={`flex-row items-center ${center ? 'justify-center' : ''}`}>
        <Text
          accessibilityRole={'header'}
          aria-level={ariaLevel ?? 1}
          className={clsx(`text-balance ${className}`, {
            'text-brand-brown-dark': !light,
            'text-brand-cream-light': light,
            'text-center': center,
            'text-5xl font-black leading-[1.1] tracking-tight md:text-5xl md:leading-[1.1] lg:text-6xl lg:leading-[1.1]':
              extraLarge,
            'text-4xl font-black md:text-5xl': hero && !extraLarge,
            'text-2xl font-black md:text-3xl lg:text-4xl': !extraLarge && !hero,
          })}
        >
          {title}
        </Text>

        {right && <View className={'ml-2 shrink-0'}>{right}</View>}

        {linkHref && (
          <Link
            href={linkHref}
            asHref={linkAsHref}
            containerClassName={'ml-3 shrink-0 flex-row items-center gap-1'}
            textClassName={'text-brand-brown/70 md:text-lg'}
            numberOfLines={1}
          >
            {linkTitle}
            <Icon icon={falAngleRight} className={'ml-0.5 size-3'} />
          </Link>
        )}
      </View>

      {subtitle && (
        <Text
          className={clsx(`max-w-prose text-pretty`, {
            'text-brand-brown-dark': !light,
            'text-brand-cream-light': light,
            'mt-6 text-lg md:text-xl': extraLarge,
            'mt-5 text-lg leading-relaxed md:text-xl': hero && !extraLarge,
            'mt-1.5 md:mt-3 md:text-lg': !extraLarge && !hero,
            'mx-auto text-center': center,
          })}
        >
          {subtitle}
        </Text>
      )}
    </View>
  );
};

const H2 = ({
  title,
  subtitle,
  linkTitle,
  linkHref,
  linkAsHref,
  right,
  className,
  marginTop = true,
  marginBottom = true,
  center = false,
  id,
}: Props<{
  title: ReactNode | string;
  subtitle?: ReactNode | string;
  linkTitle?: string;
  linkHref?: string;
  linkAsHref?: string;
  right?: ReactNode;
  marginTop?: boolean;
  marginBottom?: boolean;
  center?: boolean;
  id?: string;
}>) => {
  return (
    <View className={`${marginTop ? 'mt-9 md:mt-12' : ''} ${marginBottom ? 'mb-3 md:mb-6' : ''}`}>
      <View className={`flex-row items-center ${center ? 'w-full justify-center' : ''}`}>
        <Text
          id={id}
          accessibilityRole={'header'}
          aria-level={2}
          className={`text-brand-brown text-pretty text-xl font-bold md:text-2xl ${className} ${center ? 'text-center' : ''}`}
        >
          {title}
        </Text>

        {right && <View className={'ml-3 shrink-0'}>{right}</View>}

        {linkHref && (
          <Link
            href={linkHref}
            asHref={linkAsHref}
            containerClassName={'ml-3 shrink-0 flex-row items-center gap-1'}
            textClassName={'text-brand-brown/70 text-sm md:text-base'}
            numberOfLines={1}
          >
            {linkTitle}
            <Icon icon={falAngleRight} className={'ml-0.5 size-3'} />
          </Link>
        )}
      </View>

      {subtitle && (
        <Text
          className={`text-brand-brown-dark max-w-prose text-pretty pt-1 md:pt-2 md:text-lg ${center ? 'mx-auto text-center' : ''}`}
        >
          {subtitle}
        </Text>
      )}
    </View>
  );
};

export { H1, H2 };
